import React, {useEffect, useRef, useState} from "react";
import {useLoginBackgroundStyle} from '../utilities/loginBackground';
import {GenreIcon} from '../utilities/genreIcons';
import {Link, useNavigate} from "react-router-dom";
import {useBrandLogo} from '../utilities/genreIcons';
import {RiLockPasswordFill} from "react-icons/ri";
import { MdAlternateEmail } from "react-icons/md";
import Swal from "sweetalert2";
import SimpleReactValidator from 'simple-react-validator';

const SignupForm = () => {
    const loginBgStyle = useLoginBackgroundStyle();
    const ddLogo = useBrandLogo();
    const [username, setUsername] = useState();
    const [email, setEmail] = useState();
    const [password, setPassword] = useState();
    const [confirmPassword, setConfirmPassword] = useState();
    const [emailError, setEmailError] = useState();
    const [userNameError, setUsernameError] = useState();
    const [passwordError, setPasswordError] = useState();
    const [confirmPasswordError, setConfirmPasswordError] = useState();
    const [submitted, setSubmitted] = useState(false);
    const [showPasswordHint, setShowPasswordHint] = useState(false);
    const [resending, setResending] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState('');
    const turnstileRef = useRef(null);
    const turnstileWidgetId = useRef(null);
    const passwordHintRef = useRef(null);
    const navigate = useNavigate();

    // The Turnstile script (loaded in index.html) attaches window.turnstile async,
    // so poll briefly until it's ready rather than assuming it's there on mount.
    useEffect(() => {
        const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
        if (!siteKey) return;

        let cancelled = false;
        const tryRender = () => {
            if (cancelled) return;
            if (window.turnstile && turnstileRef.current && turnstileWidgetId.current === null) {
                turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
                    sitekey: siteKey,
                    callback: token => setTurnstileToken(token),
                    'expired-callback': () => setTurnstileToken(''),
                    'error-callback': () => setTurnstileToken('')
                });
            } else if (!window.turnstile) {
                setTimeout(tryRender, 200);
            }
        };
        tryRender();

        return () => {
            cancelled = true;
            if (window.turnstile && turnstileWidgetId.current !== null) {
                window.turnstile.remove(turnstileWidgetId.current);
                turnstileWidgetId.current = null;
            }
        };
    }, []);

    // Validation logic for email format
    const isEmailValid = (email) => {
        const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        return emailPattern.test(email);
    };

    const isPasswordValid = (password) => {
        const passwordPattern = /^(?=(.*[a-z]){3,})(?=(.*[A-Z]){2,})(?=(.*[0-9]){2,})(?=(.*[!@#$%^&*()\-__+.]){1,}).{8,}$/;
        return passwordPattern.test(password);
    };

    // Mirrors isPasswordValid, split up so the hint can show which parts are already met.
    const countMatches = (value, pattern) => ((value || '').match(pattern) || []).length;
    const passwordRules = [
        { label: 'At least 8 characters', met: (password || '').length >= 8 },
        { label: '3 lowercase letters', met: countMatches(password, /[a-z]/g) >= 3 },
        { label: '2 uppercase letters', met: countMatches(password, /[A-Z]/g) >= 2 },
        { label: '2 numbers', met: countMatches(password, /[0-9]/g) >= 2 },
        { label: '1 special character (!@#$%^&*()-_+.)', met: countMatches(password, /[!@#$%^&*()\-_+.]/g) >= 1 },
    ];

    // Dismiss the hint on outside click or Escape so it never lingers over the form.
    useEffect(() => {
        if (!showPasswordHint) return;
        const handlePointer = (e) => {
            if (passwordHintRef.current && !passwordHintRef.current.contains(e.target)) {
                setShowPasswordHint(false);
            }
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') setShowPasswordHint(false);
        };
        document.addEventListener('mousedown', handlePointer);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handlePointer);
            document.removeEventListener('keydown', handleKey);
        };
    }, [showPasswordHint]);

    const validateUsernameChange = (e) => {
        const value = e.target.value;

        // Validate if the input is not empty
        if (!value.trim()) {
            setUsernameError('This field is required.');
        } else {
            setUsernameError('');
        }
    };

    const validateEmailChange = (e) => {
        if (!isEmailValid(email)) {
            setEmailError('Invalid email format');
        } else {
            setEmailError('');
        }
    };

    const validatePasswordInput = (e) => {
        const value = e.target.value;

        // Validate if the input is not empty
        if (!value.trim()) {
            setPasswordError('This field is required.');
        } else {
            setPasswordError('');
        }
        if (!isPasswordValid(value)) {
            setPasswordError('Password does not meet requirements.');
        } else {
            setPasswordError('');
        }
    }

    const validateConfirmPassword = (e) => {
        const value = e.target.value;
        if (value !== password) {
            setConfirmPasswordError('Passwords do not match.');
        } else {
            setConfirmPasswordError('');
        }
        if (!isPasswordValid(value)) {
            setPasswordError('Password does not meet requirements.');
        } else {
            setPasswordError('');
        }
    }

    async function registerUser(credentials) {
        const res = await fetch(`${process.env.REACT_APP_API_PROXY}/auth/signup`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(credentials)
        });
        const data = await res.json();
        if (!res.ok || data.message?.toLowerCase().includes('error')) {
            throw new Error(data.message || 'Registration failed');
        }
        return data;
    }

    const handleSubmit = async e => {
        e.preventDefault();
        try {
            await registerUser({ username, password, email, turnstileToken });
            setSubmitted(true);
        } catch (err) {
            Swal.fire({
                timer: 3000,
                title: 'Registration Failed',
                text: err.message,
                icon: 'error'
            });
        } finally {
            // Tokens are single-use — reset the widget so a retry gets a fresh one.
            if (window.turnstile && turnstileWidgetId.current !== null) {
                window.turnstile.reset(turnstileWidgetId.current);
            }
            setTurnstileToken('');
        }
    }

    const handleResend = async () => {
        setResending(true);
        try {
            const res = await fetch(`${process.env.REACT_APP_API_PROXY}/auth/resend-verification`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email })
            });
            const data = await res.json();
            Swal.fire({
                timer: 3000,
                title: res.ok ? 'Email Sent' : 'Error',
                text: data.message || (res.ok ? 'Verification email resent.' : 'Could not resend email.'),
                icon: res.ok ? 'success' : 'error'
            });
        } catch {
            Swal.fire({ timer: 2000, title: 'Error', text: 'Could not resend email.', icon: 'error' });
        } finally {
            setResending(false);
        }
    }

    if (submitted) {
        return (
            <div className="login" style={loginBgStyle}>
                <div className="wrapper" style={{ textAlign: 'center' }}>
                    <img src={ddLogo} alt="DD_Logo" className="brand-logo brand-logo--lg"/>
                    <h1 className="brand-title">DropWyrm<span className="vtt">VTT</span></h1>
                    <h2 style={{ margin: '20px 0 10px', fontSize: '22px' }}>Check your email</h2>
                    <p style={{ fontSize: '14px', marginBottom: '20px', lineHeight: '1.6' }}>
                        We sent a verification link to <strong>{email}</strong>.<br/>
                        Click the link to activate your account, then come back to log in.
                    </p>
                    <button onClick={handleResend} disabled={resending} style={{ marginBottom: '16px' }}>
                        {resending ? 'Sending…' : 'Resend verification email'}
                    </button>
                    <div className="register-link">
                        <p>Already verified? <Link to="/">Log in</Link></p>
                    </div>
                </div>
                <div className="copyright-footer">
                    &copy; {new Date().getFullYear()} Virtual Realms Studios, LLC. All rights reserved.
                </div>
            </div>
        );
    }

    return (
        <div className="login" style={loginBgStyle}>
            <div className="wrapper">
                <form onSubmit={handleSubmit}>
                    <img src={ddLogo} alt="DD_Logo" className="brand-logo brand-logo--lg"/>
                    <h1 className="brand-title">DropWyrm<span className="vtt">VTT</span></h1>
                    <div className="input-box">
                        <input
                            type="text"
                            placeholder='Email'
                            onBlur={validateEmailChange}
                            onChange={e => setEmail(e.target.value)}
                        />
                        <MdAlternateEmail className="icon"/>
                        {emailError && <p>{emailError}</p>}
                    </div>
                    <div className="input-box">
                        <input
                            type="text"
                            placeholder='Username'
                            onBlur={validateUsernameChange}
                            onChange={e => setUsername(e.target.value)}
                        />
                        <GenreIcon name="username" className="icon"/>
                        {userNameError && <p>{userNameError}</p>}
                    </div>
                    <div className="input-box">
                        <input
                            type="password"
                            placeholder='Password'
                            onBlur={validatePasswordInput}
                            onChange={e => setPassword(e.target.value)}/>
                        <RiLockPasswordFill className="icon"/>
                        <div className="password-hint-wrap" ref={passwordHintRef}>
                            <button
                                type="button"
                                className="password-hint-toggle"
                                onClick={() => setShowPasswordHint(v => !v)}
                                aria-expanded={showPasswordHint}
                                aria-controls="password-hint"
                                aria-label="Password requirements"
                                title="Password requirements"
                            >
                                ?
                            </button>
                            {showPasswordHint && (
                                <div className="password-hint" id="password-hint" role="tooltip">
                                    <ul>
                                        {passwordRules.map(rule => (
                                            <li key={rule.label} className={rule.met ? 'met' : undefined}>
                                                <span className="mark" aria-hidden="true">{rule.met ? '✓' : '•'}</span>
                                                {rule.label}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                        {passwordError && <p>{passwordError}</p>}
                    </div>
                    <div className="input-box">
                        <input
                            type="password"
                            placeholder='Confirm Password'
                            onChange={e => setConfirmPassword(e.target.value)}
                            onBlur={validateConfirmPassword}
                        />
                        <RiLockPasswordFill className="icon"/>
                        {confirmPasswordError && <p>{confirmPasswordError}</p>}
                    </div>
                    {process.env.REACT_APP_TURNSTILE_SITE_KEY && (
                        <div
                            ref={turnstileRef}
                            style={{ margin: '12px 0', display: 'flex', justifyContent: 'center' }}
                        />
                    )}
                    <button
                        type="submit"
                        disabled={!!process.env.REACT_APP_TURNSTILE_SITE_KEY && !turnstileToken}
                    >
                        Register
                    </button>
                    <div className="register-link" style={{ marginTop: '16px' }}>
                        <p>Already have an account? <Link to="/">Log in</Link></p>
                        <p style={{ fontSize: '0.78rem', opacity: 0.8 }}>
                            By registering you agree to our <Link to="/terms">Terms of Use</Link> and{' '}
                            <Link to="/privacy">Privacy Policy</Link>.
                        </p>
                    </div>
                </form>
            </div>
            <div className="copyright-footer">
                &copy; {new Date().getFullYear()} Virtual Realms Studios, LLC. All rights reserved.
            </div>
        </div>
    )
}

export default SignupForm;